'use client';

import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';
import {
  effectiveAgesFromTeamConfig,
  parseTournamentAgeApiPayload,
  tournamentAgeInputsShowRangeConflict,
  type TournamentAgeDraftSlice,
  type TournamentAgeRestrictionPatch,
} from '@/utils/tournament-age';

export type TournamentAgeRestrictionFieldsHandle = {
  getApiPayload: () => { minAge: number; maxAge: number } | null;
};

export type TournamentAgeRestrictionFieldsProps = {
  value: TournamentAgeDraftSlice;
  onChange: (patch: TournamentAgeRestrictionPatch) => void;
  helpText: string;
  /** Used by the edit modal to disable Save until the current inputs form a valid API payload. */
  onApiPayloadValidityChange?: (valid: boolean) => void;
};

export const TournamentAgeRestrictionFields = forwardRef<
  TournamentAgeRestrictionFieldsHandle,
  TournamentAgeRestrictionFieldsProps
>(function TournamentAgeRestrictionFields(
  { value, onChange, helpText, onApiPayloadValidityChange },
  ref,
) {
  const baseId = useId();
  const minAgeRestrictionEnabled = value?.minAgeRestrictionEnabled === true;
  const maxAgeRestrictionEnabled = value?.maxAgeRestrictionEnabled === true;

  const [minAgeInput, setMinAgeInput] = useState('');
  const [maxAgeInput, setMaxAgeInput] = useState('');

  useEffect(() => {
    if (minAgeRestrictionEnabled && value?.minAge !== undefined) {
      setMinAgeInput(String(value.minAge));
    } else if (!minAgeRestrictionEnabled) {
      setMinAgeInput('');
    }
  }, [minAgeRestrictionEnabled, value?.minAge]);

  useEffect(() => {
    if (maxAgeRestrictionEnabled && value?.maxAge !== undefined) {
      setMaxAgeInput(String(value.maxAge));
    } else if (!maxAgeRestrictionEnabled) {
      setMaxAgeInput('');
    }
  }, [maxAgeRestrictionEnabled, value?.maxAge]);

  const committedOrderInvalid = useMemo(() => {
    const e = effectiveAgesFromTeamConfig(value ?? null);
    return e.minAge > e.maxAge;
  }, [value]);

  const inputsConflict = useMemo(
    () =>
      tournamentAgeInputsShowRangeConflict(
        minAgeRestrictionEnabled,
        maxAgeRestrictionEnabled,
        minAgeInput,
        maxAgeInput,
      ),
    [
      minAgeRestrictionEnabled,
      maxAgeRestrictionEnabled,
      minAgeInput,
      maxAgeInput,
    ],
  );

  const apiPayload = useMemo(
    () =>
      parseTournamentAgeApiPayload(
        minAgeRestrictionEnabled,
        maxAgeRestrictionEnabled,
        minAgeInput,
        maxAgeInput,
      ),
    [
      minAgeRestrictionEnabled,
      maxAgeRestrictionEnabled,
      minAgeInput,
      maxAgeInput,
    ],
  );

  useEffect(() => {
    onApiPayloadValidityChange?.(apiPayload !== null);
  }, [apiPayload, onApiPayloadValidityChange]);

  useImperativeHandle(
    ref,
    () => ({
      getApiPayload: () => apiPayload,
    }),
    [apiPayload],
  );

  const commitMinAge = () => {
    if (!minAgeRestrictionEnabled) return;
    const trimmed = minAgeInput.trim();
    if (trimmed === '') {
      if (value?.minAge !== undefined) {
        setMinAgeInput(String(value.minAge));
      }
      return;
    }
    const n = parseInt(trimmed, 10);
    if (!Number.isFinite(n)) {
      setMinAgeInput(
        value?.minAge !== undefined ? String(value.minAge) : '',
      );
      return;
    }
    const v = Math.max(0, Math.floor(n));
    onChange({ minAge: v });
    setMinAgeInput(String(v));
  };

  const commitMaxAge = () => {
    if (!maxAgeRestrictionEnabled) return;
    const trimmed = maxAgeInput.trim();
    if (trimmed === '') {
      if (value?.maxAge !== undefined) {
        setMaxAgeInput(String(value.maxAge));
      }
      return;
    }
    const n = parseInt(trimmed, 10);
    if (!Number.isFinite(n)) {
      setMaxAgeInput(
        value?.maxAge !== undefined ? String(value.maxAge) : '',
      );
      return;
    }
    const v = Math.max(0, Math.floor(n));
    onChange({ maxAge: v });
    setMaxAgeInput(String(v));
  };

  const setMinRestriction = (enabled: boolean) => {
    if (enabled) {
      onChange({
        minAgeRestrictionEnabled: true,
        minAge: undefined,
      });
      setMinAgeInput('');
    } else {
      onChange({
        minAgeRestrictionEnabled: false,
        minAge: undefined,
      });
      setMinAgeInput('');
    }
  };

  const setMaxRestriction = (enabled: boolean) => {
    if (enabled) {
      onChange({
        maxAgeRestrictionEnabled: true,
        maxAge: undefined,
      });
      setMaxAgeInput('');
    } else {
      onChange({
        maxAgeRestrictionEnabled: false,
        maxAge: undefined,
      });
      setMaxAgeInput('');
    }
  };

  const showAlert = committedOrderInvalid || inputsConflict;

  return (
    <div className="space-y-6">
      {showAlert && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            La edad mínima no puede ser mayor que la edad máxima
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-3">
        <div className="flex items-start gap-3">
          <Checkbox
            id={`${baseId}-min-age`}
            checked={minAgeRestrictionEnabled}
            onCheckedChange={(c) => setMinRestriction(c === true)}
            className="mt-1"
          />
          <div className="flex-1 space-y-2">
            <Label
              htmlFor={`${baseId}-min-age`}
              className="cursor-pointer font-medium"
            >
              Restringir edad mínima
            </Label>
            <Input
              id={`${baseId}-min-age-input`}
              type="number"
              min={0}
              step={1}
              disabled={!minAgeRestrictionEnabled}
              placeholder={minAgeRestrictionEnabled ? 'Edad mínima' : ''}
              value={minAgeInput}
              onChange={(e) => setMinAgeInput(e.target.value)}
              onBlur={commitMinAge}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  (e.currentTarget as HTMLInputElement).blur();
                }
              }}
            />
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Checkbox
            id={`${baseId}-max-age`}
            checked={maxAgeRestrictionEnabled}
            onCheckedChange={(c) => setMaxRestriction(c === true)}
            className="mt-1"
          />
          <div className="flex-1 space-y-2">
            <Label
              htmlFor={`${baseId}-max-age`}
              className="cursor-pointer font-medium"
            >
              Restringir edad máxima
            </Label>
            <Input
              id={`${baseId}-max-age-input`}
              type="number"
              min={0}
              step={1}
              disabled={!maxAgeRestrictionEnabled}
              placeholder={maxAgeRestrictionEnabled ? 'Edad máxima' : ''}
              value={maxAgeInput}
              onChange={(e) => setMaxAgeInput(e.target.value)}
              onBlur={commitMaxAge}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  (e.currentTarget as HTMLInputElement).blur();
                }
              }}
            />
          </div>
        </div>

        <p className="text-xs text-gray-500">{helpText}</p>
      </div>
    </div>
  );
});

TournamentAgeRestrictionFields.displayName = 'TournamentAgeRestrictionFields';
